import React, { useState, useEffect } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { toTranslationKey, tForComponent } from '@/utils/translations';
import QuestionnaireInput from './QuestionnaireInput';
import { formatValue } from './utils';
import EmptySection from './EmptySection';
import { useUpdateQuestionnaireStore } from '@/stores/useUpdateQuestionnaire';
import { OtherCredits as OtherCreditsType, Maybe } from '@/graphql/__generated__/credit-requests';
import QuestionnaireEditableInput from './QuestionnaireEditableInput';

interface OtherCreditsProps {
	data: Maybe<OtherCreditsType> | undefined;
	title: string;
	isEditing?: boolean;
	inputErrors?: Record<string, string>;
}

enum inputErrorMapping {
	currentTotalDebt = 'current_total_debt',
	monthlyPaymentToCredits = 'monthly_payment_to_credits'
}

const OtherCredits: React.FC<OtherCreditsProps> = ({ data, title, isEditing, inputErrors }) => {
	const t = useTranslations('MicrocreditRequestQuestionnaire');
	const { otherCredits, updateOtherCredits } = useUpdateQuestionnaireStore();
	const [formData, setFormData] = useState(otherCredits as OtherCreditsType);

	useEffect(() => {
		setFormData(otherCredits);
	}, [otherCredits]);

	const handleChange = (key: string, value: any) => {
		setFormData((prevData: any) => ({
			...prevData,
			[key]: value
		}));
		updateOtherCredits({ [key]: value });
	};
	const editableFields = () => {
		return (
			<>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'currentTotalDebt'}
						value={formData['currentTotalDebt'] || data?.currentTotalDebt || ''}
						onChange={(newValue) => handleChange('currentTotalDebt', newValue)}
						error={inputErrors?.[inputErrorMapping.currentTotalDebt] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'monthlyPaymentToCredits'}
						value={formData['monthlyPaymentToCredits'] || data?.monthlyPaymentToCredits || ''}
						onChange={(newValue) => handleChange('monthlyPaymentToCredits', newValue)}
						error={inputErrors?.[inputErrorMapping.monthlyPaymentToCredits] || ''}
						type="money"
					/>
				</div>
			</>
		);
	};

	return (
		<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
			<div className={`flex flex-row items-center mb-4`}>
				<div className="grow">
					<Typography weight="semibold">{t(toTranslationKey(title))}</Typography>
				</div>
			</div>
			{data ? (
				<div className="flex flex-row flex-wrap w-full">
					{isEditing ? (
						editableFields()
					) : (
						<>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'currentTotalDebt'}
									value={formatValue(data.currentTotalDebt, t)}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'monthlyPaymentToCredits'}
									value={formatValue(data.monthlyPaymentToCredits, t)}
								/>
							</div>
						</>
					)}
				</div>
			) : (
				<EmptySection t={tForComponent(t)} />
			)}
		</div>
	);
};

export default OtherCredits;
